import React, { useState, useEffect } from 'react';

import SwipeableViews from 'react-swipeable-views-react-18-fix';
import dayjs from 'dayjs';

import WizardWrapper from './WizardWrapper';
import ConfirmDialog from './ConfirmDialog';
import AssignmentTaskType from './AssignmentTaskType';
import AssignmentBook from './AssignmentBook';
import AssignmentPages from './AssignmentPages';
import AssignmentMetricTask from './AssignmentMetricTask';
import AssignmentInstructions from './AssignmentInstructions';
import AssignmentDate from './AssignmentDate';
import AssignmentSummary from './AssignmentSummary';

import { useAppState } from './AppStateContext';

import { hexToBinaryArray, indexIsSet, setArrayIndex, binaryArrayToHex } from './bigIntReplacement';
import { getPageOffset } from './helpers.js';

const AssignmentWizard = ({ open, onClose, classUUID, assignment }) => {
    const { apiGet, apiPost, apiDelete } = useAppState();

    const [activeStep, setActiveStep] = useState(0);
    const [disabledButtons, setDisabledButtons] = useState(false);

    const [editMode, setEditMode] = useState(true);

    const [taskType, setTaskType] = useState(null);
    const [numWords, setNumWords] = useState(0);
    const [numMinutes, setNumMinutes] = useState(0);

    const [instructions, setInstructions] = useState("");
    const [dueDate, setDueDate] = useState(dayjs());

    const [books, setBooks] = useState([]);
    const [booksLoading, setBooksLoading] = useState(true);
    const [selectedBookId, setSelectedBookId] = useState(null);
    const [book, setBook] = useState(null);
    const [selectedChapters, setSelectedChapters] = useState([]);

    const [errorMessage, setErrorMessage] = useState("");
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

    useEffect(() => {
        if (!open) {
            setErrorMessage("");
        }

        if (open && assignment) {
            const task = assignment.tasks[0];
            setTaskType(task.type);
            setNumWords(task.words ? task.words : 0);
            setNumMinutes(task.minutes ? task.minutes : 0);
            setInstructions(assignment.instructions ? assignment.instructions : '');
            setDueDate(dayjs(assignment.due_date));
            if (task.book) {
                const book = task.book;
                const pageOffset = getPageOffset(book);
                const bitmap = hexToBinaryArray(task.pages || '0');
                let selectedChapters = [];
                for (let chapter of book.chapters) {
                    let allBitsSet = true;
                    chapter.tokens = 0;
                    chapter.duration = 0;
                    for (let page of chapter.pages) {
                        if (!indexIsSet(bitmap, page.number - pageOffset)) {
                            allBitsSet = false;
                        }
                        chapter.tokens += page.tokens;
                        chapter.duration+= page.duration;
                    }
                    if (allBitsSet) {
                        selectedChapters.push(chapter);
                    }
                }
                setSelectedBookId(book.id);
                setBook(book);
                setSelectedChapters(selectedChapters);
            }
            setActiveStep(5);
            setEditMode(false);
        }
    }, [open]);

    const handleBack = (event) => {
        event.stopPropagation();
        setActiveStep(prevActiveStep => prevActiveStep - (prevActiveStep ===  3 && taskType === 'metric' ? 2 : 1));
    };

    const fetchBook = async (id) => {
        setErrorMessage("");
        try {
            setBook(null);

            const response = await apiGet(`books/${id}`);
            const newBook = response.data

            if (newBook.duration) newBook.duration = Number(newBook.duration);
            if (newBook.tokens) newBook.tokens = Number(newBook.tokens);

            newBook.chapters = newBook.chapters.map(chapter => {
                let totalTokens = 0;
                let totalSeconds = 0;
                for (let i = 0; i < chapter.pages.length; i++) {
                    totalTokens += chapter.pages[i].tokens ? chapter.pages[i].tokens : 0;
                    totalSeconds += chapter.pages[i].duration ? chapter.pages[i].duration : 0;
                }
                chapter.tokens = totalTokens;
                chapter.duration = totalSeconds;
                chapter.pages = chapter.pages.map(page => page.number);
                return chapter;
            });

            setBook(newBook);
        } catch (err) {
            setErrorMessage('An error occurred while trying to load the book.');
        }
    };

    useEffect(() => {
        if (selectedBookId !== null && selectedBookId !== book?.id) {
            setSelectedChapters([]);
            fetchBook(selectedBookId);
        }
    }, [selectedBookId]);

    const fetchBooks = async () => {
        setErrorMessage("");
        setBooksLoading(true);
        try {
            const response = await apiGet('books');
            const newBooks = response.data.map(book => {
                if (book.duration) book.duration = Number(book.duration);
                if (book.tokens) book.tokens = Number(book.tokens);
                return book;
            });
            setBooks(newBooks);
            setBooksLoading(false);
        } catch (err) {
            setErrorMessage('An error occurred while trying to load the book list.');
            setBooksLoading(false);
        }
    };

    useEffect(() => {
        if (taskType === 'text') {
            fetchBooks();
        }
    }, [taskType]);

    const handleEdit = () => {
        setActiveStep(0);
        setEditMode(true);
    };

    const handleSave = async () => {
        setErrorMessage("");
        const failureMessage = assignment
            ? "An error occurred while trying to save the assignment."
            : "An error occurred while trying to create the assignment.";

        let task = null;
        if (taskType === 'text') {
            const book_id = selectedBookId;
            if (!book || book_id !== book.id) {
                setErrorMessage(failureMessage);
                return;
            }
            const pageOffset = getPageOffset(book);
            let bitmap = [];
            for (let chapter of selectedChapters){
                for (let page of chapter.pages) {
                    setArrayIndex(bitmap, (page.number === undefined ? page : page.number) - pageOffset);
                }
            }
            const pages = binaryArrayToHex(bitmap);

            task = { taskType, book_id, pages };
        } else {
            task = { taskType, numWords, numMinutes };
        }

        const assignmentData = { classUUID, instructions, dueDate: dueDate.format('YYYY-MM-DD'), tasks: [task] };
        if (assignment) {
            assignmentData.uuid = assignment.uuid; 
        }

        try {
            await apiPost('assignments', assignmentData);
            handleClose(null, 'created');
        } catch (error) {
            setErrorMessage(failureMessage);
        }
    };

    const handleDelete = async () => {
        setErrorMessage("");
        try {
            await apiDelete(`assignments/${assignment.uuid}`);
            handleClose(null, 'created');
        } catch (error) {
            setErrorMessage("An error occurred while trying to delete the assignment.");
        }
    };

    const handleClose = (event, reason) => {
        setActiveStep(0);
        setEditMode(true);

        setTaskType(null);
        setNumWords(0);
        setNumMinutes(0);
        setInstructions("");
        setDueDate(dayjs());
        setBooks([]);
        setSelectedBookId(null);
        setBook(null);
        setSelectedChapters([]);

        onClose(reason === 'created');
    };

    const handleClick = (event) => {
        if (activeStep === 1 && taskType === 'text') {
            setSelectedBookId(null);
        }
    };

    // The wrapper sizes the dialog to its content, capped to the viewport;
    // the book list is the one step that also needs more width.
    const wrapperSx = taskType === 'text' && activeStep === 1 ? {
        width: '90%',
        maxWidth: '1200px',
    } : {};

    return (
        <WizardWrapper
            open={open}
            onClose={handleClose}
            onClick={handleClick}
            onBack={handleBack}
            canGoBack={activeStep > 0 && editMode}
            sx={wrapperSx}
            disabledButtons={disabledButtons}
        >
            <SwipeableViews index={activeStep}>
                <AssignmentTaskType
                    taskType={taskType}
                    setTaskType={setTaskType}
                    setActiveStep={setActiveStep}
                />
                { taskType === 'metric' ?
                    <AssignmentMetricTask
                        numWords={numWords}
                        setNumWords={setNumWords}
                        numMinutes={numMinutes}
                        setNumMinutes={setNumMinutes}
                        setActiveStep={setActiveStep}
                    />
                :
                    <AssignmentBook
                        setActiveStep={setActiveStep}
                        books={books}
                        selectedBookId={selectedBookId}
                        setSelectedBookId={setSelectedBookId}
                        setDisabledButtons={setDisabledButtons}
                        booksLoading={booksLoading}
                        errorMessage={errorMessage}
                    />
                }
                { taskType === 'metric' ?
                    <div/>
                :
                    <AssignmentPages
                        setActiveStep={setActiveStep}
                        book={book}
                        selectedChapters={selectedChapters}
                        setSelectedChapters={setSelectedChapters}
                        errorMessage={errorMessage}
                    />
                }
                <AssignmentInstructions
                    instructions={instructions}
                    setInstructions={setInstructions}
                    setActiveStep={setActiveStep}
                />
                <AssignmentDate
                    dueDate={dueDate}
                    setDueDate={setDueDate}
                    setActiveStep={setActiveStep}
                />
                <AssignmentSummary
                    editMode={editMode}
                    assignment={assignment}
                    taskType={taskType}
                    numWords={numWords}
                    numMinutes={numMinutes}
                    book={book}
                    selectedChapters={selectedChapters}
                    instructions={instructions}
                    dueDate={dueDate}
                    handleEdit={handleEdit}
                    handleSave={handleSave}
                    handleDelete={() => setConfirmDeleteOpen(true)}
                    handleCancel={handleClose}
                    errorMessage={errorMessage}
                />
            </SwipeableViews>
            <ConfirmDialog
                open={confirmDeleteOpen}
                title="Delete assignment"
                message="Do you really want to delete this assignment?"
                confirmLabel="Delete"
                onConfirm={() => { setConfirmDeleteOpen(false); handleDelete(); }}
                onCancel={() => setConfirmDeleteOpen(false)}
            />
        </WizardWrapper>
    );
};

export default AssignmentWizard;
